import React, {useEffect, useState} from "react";

import {useDarkMode} from "../../contexts/darkModeContext";
import {useNotification} from "../Notifications/NotificationProvider";
import course from "../../PFC_Images/course.jpg";
import {Button} from "@headlessui/react";
import {PaperAirplaneIcon, SunIcon} from "@heroicons/react/24/outline";
import {formatTimeStampDate, getCardinalDirection} from "../GLOBAL/GlobalFunctions";
import WeatherIcons from "./WeatherIcons";

const WeatherWidget = () => {
    
    const { triggerNotification } = useNotification();
    const { darkMode } = useDarkMode();
    
    const [weatherCollapse, setWeatherCollapse] = useState(false);
    
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [weatherData, setWeatherData] = useState(null);
    
    useEffect(() => {
        const fetchWeather = () => {
            if (!navigator.geolocation) {
                triggerNotification('Geolocation is not supported by your browser', 'error');
                return;
            }
            navigator.geolocation.getCurrentPosition(async (position) => {
                const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                setTime(currentTime);

                const currentDate = new Date().toLocaleDateString();
                setDate(currentDate);

                const { latitude, longitude } = position.coords;
                try {
                    const response = await fetch(
                        `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&units=imperial&appid=4b0334fba82a13fbb111ce08c64a7b02`
                    );
                    if (!response.ok) throw new Error('Failed to fetch weather');
                    const data = await response.json();
                    setWeatherData(data);
                } catch (error) {
                    console.error(error);
                    triggerNotification('Failed to load weather data', 'error');
                }
            }, (error) => {
                console.error(error);
                triggerNotification('Unable to retrieve your location', 'error');
            });
        };

        fetchWeather();

        // Update time every minute
        const timeInterval = setInterval(() => {
            const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            setTime(currentTime);
        }, 60000);

        // Refresh weather every 10 minutes
        const weatherInterval = setInterval(() => {
            fetchWeather();
        }, 600000);

        return () => {
            clearInterval(timeInterval);
            clearInterval(weatherInterval);
        };
    }, []);
 
    return (
        
        <div className={`hidden lg:flex items-center`}>
            
            {/*Weather Section*/}
            <div className={`relative overflow-hidden rounded-r-lg transition-all duration-700 flex items-center justify-center ` + `${weatherCollapse ? 'w-14' : 'w-72'} h-14 ${weatherData?.weather?.[0]?.icon?.includes("n") ? 'bg-black' : 'bg-gray-500'}`}>
                
                {/* Clickable icon toggles collapse (keeps original behavior) */}
                <div
                    className={`relative z-10 w-14 h-14 flex items-center ${darkMode ? 'bg-darkBgColor' : 'bg-bgcolorLight'}`}>
                    <div className="flex items-center h-full w-full justify-center">
                        <Button
                            onClick={() => setWeatherCollapse(!weatherCollapse)}
                            className="flex items-center justify-center size-14 rounded hover:bg-white/5 transition"
                            aria-label="toggle weather collapse"
                        >
                            <div className="flex items-center justify-center w-full mt-4 relative">
                                <div
                                    className="absolute w-10 h-10 rounded-full blur-md opacity-30"
                                    style={{
                                        backgroundColor:
                                            weatherData?.main?.temp > 85
                                                ? "#f97316"
                                                : weatherData?.main?.temp < 40
                                                    ? "#3b82f6"
                                                    : "#e5e7eb"
                                    }}
                                />
                                <div className="text-2xl flex mt-1 font-bold z-10 transition-all duration-700 text-white">
                                    <span className={``}>{((weatherData?.main?.temp) ?? 0).toFixed(0)}</span>
                                    <span className={`text-white text-sm`}>°F</span>
                                </div>
                            </div>
                            
                            <div className={`absolute top-0.5 left-0.5`}>
                                <WeatherIcons selectedWeather={weatherData?.weather?.[0]?.icon} size={`6`}/>
                            </div>
                        
                        </Button>
                    </div>
                
                </div>
                
                {/*Rest of Layout*/}
                <div className="flex-1 h-full overflow-hidden">
                    <div className="w-full h-full transition-transform duration-700">
                        
                        {/* EXPANDED ROW: purely graphical components, no labels/background */}
                        <div className="w-full h-14 flex items-center px-2 justify-between relative overflow-hidden">
                            
                            {/* --- Soft Gradient Background (subtle) --- */}
                            <div
                                className="absolute inset-0 opacity-40"
                                style={{
                                    background: weatherData?.weather?.[0]?.main === "Clear"
                                        ? "linear-gradient(90deg, #fcd34d, #f97316)"
                                        : weatherData?.weather?.[0]?.main === "Clouds"
                                            ? "linear-gradient(90deg, #93c5fd, #60a5fa)"
                                            : weatherData?.weather?.[0]?.main === "Rain"
                                                ? "linear-gradient(90deg, #3b82f6, #1e3a8a)"
                                                : weatherData?.weather?.[0]?.main === "Snow"
                                                    ? "linear-gradient(90deg, #e0f2fe, #bae6fd)"
                                                    : "linear-gradient(90deg, #0f172a, #1e293b)"
                                }}
                            />
                            
                            {/* --- Decorative Atmosphere Shapes (very subtle) --- */}
                            <div className="absolute inset-0 opacity-15 pointer-events-none">
                                {/* Soft waves */}
                                <svg viewBox="0 0 1440 100" className="absolute bottom-0 w-full h-6">
                                    <path
                                        d="M0,40 C200,80 400,0 600,40 C800,80 1000,0 1200,40 C1400,80 1600,0 1800,40 L1800,120 L0,120 Z"
                                        fill="white"
                                    />
                                </svg>
                                
                                {/* Animated Star Background for Night */}
                                {weatherData?.weather?.[0]?.icon?.includes("n") && (
                                    <AnimatedStars count={40}/>
                                )}
                            
                            
                            </div>
                            
                            {/* ---------------- GRAPHICAL CONTENT ONLY ---------------- */}
                            
                            {/* Humidity with glow */}
                            <div className="flex items-center justify-center relative">
                                <div
                                    className="hidden absolute w-10 h-10 rounded-full blur-md opacity-30"
                                    style={{
                                        backgroundColor:
                                            weatherData?.main?.temp > 85
                                                ? "#f97316"
                                                : weatherData?.main?.temp < 40
                                                    ? "#3b82f6"
                                                    : "#e5e7eb"
                                    }}
                                />
                                
                                {/*Humidity*/}
                                <div className="rotate-90 flex items-center justify-center relative">
                                    <div className="absolute w-10 h-10 rounded-full bg-white/10 blur-sm"/>
                                    <svg viewBox="0 0 36 36" className="w-10 h-10 transform -rotate-90 z-10">
                                        <path
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            fill="none"
                                            stroke="#ffffff30"
                                            strokeWidth="2.5"
                                        />
                                        <path
                                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                            fill="none"
                                            stroke="#3b82f6"
                                            strokeWidth="2.5"
                                            strokeDasharray="100"
                                            strokeDashoffset={`${100 - Math.min(Math.max(Number(weatherData?.main?.humidity || 0), 0), 100)}`}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    
                                    <div
                                        className="absolute -rotate-90 text-white text-sm font-bold z-10 transition-all duration-700">
                                        {((weatherData?.main?.humidity) ?? 0).toFixed(0)}%
                                    </div>
                                
                                </div>
                            
                            </div>
                            
                            {/* Wind with glow */}
                            <div className="flex items-center space-x-2">
                                {/* Compass */}
                                <div className="relative w-10 h-10 flex items-center justify-center">
                                    {/* Gradient background */}
                                    <div
                                        className="absolute inset-0 rounded-full"
                                        style={{
                                            background: 'radial-gradient(circle at center, rgba(255,255,255,0.05), rgba(0,0,0,0.2))'
                                        }}
                                    ></div>
                                    
                                    {/* Compass ticks */}
                                    {Array.from({length: 8}).map((_, i) => {
                                        const isCardinal = i % 2 === 0; // N, E, S, W are longer
                                        const tickLength = isCardinal ? 3 : 2; // px
                                        const radius = 20; // distance from center to edge of tick
                                        const angle = i * 45; // 0, 45, 90, ..., 315
                                        
                                        return (
                                            <div
                                                key={i}
                                                className="absolute bg-white"
                                                style={{
                                                    width: 1,
                                                    height: tickLength,
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: `rotate(${angle}deg) translateY(-${radius}px)`,
                                                    transformOrigin: 'center center',
                                                    borderRadius: '1px',
                                                }}
                                            />
                                        );
                                    })}
                                    
                                    {/* Wind arrow */}
                                    <div
                                        className="absolute size-14 flex items-center justify-center text-red-700 transition-transform duration-700 ease-in-out"
                                        // style={{transform: `rotate(${weatherData?.wind?.deg ?? 0}deg)`}}
                                    >
                                        <PaperAirplaneIcon
                                            className={`size-4`}
                                            style={{
                                                transform: `rotate(${(weatherData?.wind?.deg ?? 0) - 90}deg)`, // subtract 90 so east is 0°
                                                transition: 'transform 0.5s ease-in-out'
                                            }}
                                        />
                                        <path d="M12 2 L16 12 L12 10 L8 12 Z" fill="currentColor"/>
                                    </div>
                                    
                                    
                                    {/* Center direction label */}
                                    <div className="absolute text-xs font-bold text-gray-200 mt-7">
                                        {getCardinalDirection(weatherData?.wind?.deg)}
                                    </div>
                                </div>
                                
                                {/* Wind speed next to compass */}
                                <div className="text-xs font-bold text-white">
                                    <span>{((weatherData?.wind?.speed) ?? 0).toFixed(0)}</span>
                                    <span className="text-gray-300 text-xxs ml-0.5">mph</span>
                                </div>
                            </div>
                            
                            {/*Left Hand Side*/}
                            <div className={`flex flex-col justify-end items-end text-white`}>
                                <div className={`text-[1.25rem] font-bold line-clamp-1`}>{time}</div>
                                <div className={`text-xs -mt-2.5 line-clamp-1`}>{formatTimeStampDate(date)}</div>
                                <span className={`text-xs -mt-0.5 line-clamp-1`}>{weatherData?.name ?? '--'}, {weatherData?.sys?.country ?? '--'}</span>
                            </div>
                        
                        
                        </div>
                    
                    </div>
                </div>
            
            </div>
        
        </div>
    
    )
}

export default WeatherWidget;

// AnimatedStars component for animated star background
export const AnimatedStars = ({count = 40}) => {
    // Generate stars only once per mount for consistent layout
    const [stars] = useState(() =>
        Array.from({length: count}).map((_, i) => {
            // Star properties: size, position, opacity, animation duration/delay
            const size = Math.random() * 2.5 + 2; // 2px to 4.5px (bigger stars)
            const top = Math.random() * 100; // %
            const left = Math.random() * 100; // %
            const opacity = Math.random() * 0.5 + 0.6; // 0.6 to 1.1 (brighter stars)
      const duration = Math.random() * 2 + 2.5; // 2.5s to 4.5s
      const delay = Math.random() * 4; // 0 to 4s
      return { size, top, left, opacity, duration, delay, key: i };
    })
  );
  return (
    <>
      {/* Twinkle animation CSS */}
      <style>
        {`
        @keyframes twinkle-star {
          0%   { opacity: var(--star-opacity); }
          40%  { opacity: calc(var(--star-opacity) * 0.6); }
          60%  { opacity: calc(var(--star-opacity) * 1.1); }
          100% { opacity: var(--star-opacity); }
        }
        `}
      </style>
      {stars.map(star => (
        <div
          key={star.key}
          className="absolute rounded-full bg-white pointer-events-none"
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            top: `${star.top}%`,
            left: `${star.left}%`,
            opacity: star.opacity,
            filter: 'blur(0.3px)',
            boxShadow: '0 0 6px rgba(255,255,255,0.8)',
            // Use CSS variables for opacity for animation
            '--star-opacity': star.opacity,
            animation: `twinkle-star ${star.duration}s ${star.delay}s infinite ease-in-out`
          }}
        />
      ))}
    </>
  );
};
